/**
 * Login modal — canonical customer realm only (requirements-v1 §3 Option A,
 * H-2026-10-16-175). Providers per DP-2: email-code is the primary entry,
 * Google uses the /authorize authorization-code redirect, password is the
 * secondary path (login via /login/password, signup via H-174's
 * /signup/password).
 *
 * Success path: saveAuth(user, token) -> AUTH_CHANGED event -> close.
 * Email-code flow: email -> "Email me a sign-in code" (begin) -> 6-digit
 * code -> "Sign in" (verify). Server error messages are surfaced verbatim
 * (C-1 pattern: surface the server message, never fake a "sent" state).
 *
 * The storefront can pass a Shopify customer email hint (postMessage
 * SHOPIFY_CUSTOMER_HINT -> store 'auth.emailHint', or ?email_hint=) — it is
 * ONLY a UX prefill, never a credential.
 *
 * Subscribes to store('auth.showLoginModal') and toggles the overlay.
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { createLogger } from '../../utils/logger.js'
import {
  loginPassword,
  signupPassword,
  beginEmailCode,
  verifyEmailCode,
} from '../../api/index.js'
import {
  createCreatorHubCanonicalAuthorizeUrl,
  fetchCreatorHubOauthProviders,
  recordCreatorAuthMetric,
} from './canonical-client.js'
import { saveAuth } from './index.js'

const log = createLogger('auth-modal')

let overlayEl = null
let mode = 'login' // 'login' | 'register' | 'password' | 'email-code'
let submitting = false

// Same storage keys canonical-client.js uses so the refresh grant keeps
// working after any sign-in path.
const REFRESH_KEY = 'mms_customer_refresh_token'
const EXPIRES_KEY = 'mms_token_expires'

function escapeHtml(value) {
  return String(value).replace(
    /[<>"&]/g,
    (c) => ({ '<': '&lt;', '>': '&gt;', '"': '&quot;', '&': '&amp;' })[c]
  )
}

// UX prefill only — postMessage hint from the storefront first, then the
// ?email_hint= URL param. Never treated as proof of identity.
function emailHint() {
  const fromStore = store.getState('auth.emailHint')
  if (fromStore) return String(fromStore)
  try {
    return new URLSearchParams(window.location.search).get('email_hint') || ''
  } catch {
    return ''
  }
}

const LOGO_SVG = `
  <svg viewBox="0 0 344 232" aria-label="mamamiya">
    <g fill="oklch(0.72 0.19 150)">
      <path d="M108,8H76A32,32,0,0,0,76,72H108Z"/>
      <path d="M108,8H140A32,32,0,0,1,140,72H108Z"/>
      <path d="M108,72H76A32,32,0,0,0,76,136H108Z"/>
      <circle cx="140" cy="104" r="32"/>
      <path d="M108,136H72A36,36,0,1,0,108,172Z"/>
      <path d="M236,8H268A32,32,0,0,1,268,72H236Z"/>
      <path d="M236,8H204A32,32,0,0,0,204,72H236Z"/>
      <path d="M236,72H268A32,32,0,0,1,268,136H236Z"/>
      <circle cx="204" cy="104" r="32"/>
      <path d="M236,136H272A36,36,0,1,1,236,172Z"/>
    </g>
  </svg>
`

function renderLogin() {
  return `
    <div class="auth-tabs" role="tablist">
      <button class="auth-tab" role="tab" data-active="true" data-mode="login" type="button">Sign in</button>
      <button class="auth-tab" role="tab" data-active="false" data-mode="register" type="button">Sign up</button>
    </div>
    <div class="auth-head">
      <h2 class="auth-title">Welcome back</h2>
      <p class="auth-sub">Save designs, sync your library, and pick up where you left off.</p>
    </div>
    <div class="auth-body">
      <div class="auth-oauth-list" data-oauth-list>
        <button class="auth-oauth" type="button" data-action="google" data-provider="google">
          <span class="auth-g-mark" aria-hidden="true"></span>
          <span>Continue with Google</span>
        </button>
      </div>
      <div class="auth-divider">or</div>
      <div class="auth-field">
        <label for="auth-email">Email</label>
        <input id="auth-email" type="email" autocomplete="email" placeholder="you@studio.com" />
      </div>
      <button class="auth-submit" type="button" data-action="email-code">Email me a sign-in code</button>
      <p class="auth-foot"><button type="button" data-action="show-password">Sign in with a password instead</button></p>
    </div>
    <p class="auth-foot">No account? <button type="button" data-action="switch-register">Create one</button></p>
  `
}

function renderPasswordLogin() {
  return `
    <div class="auth-tabs" role="tablist">
      <button class="auth-tab" role="tab" data-active="true" data-mode="login" type="button">Sign in</button>
      <button class="auth-tab" role="tab" data-active="false" data-mode="register" type="button">Sign up</button>
    </div>
    <div class="auth-head">
      <h2 class="auth-title">Sign in with password</h2>
      <p class="auth-sub">For accounts you created with a password.</p>
    </div>
    <div class="auth-body">
      <div class="auth-field">
        <label for="auth-email">Email</label>
        <input id="auth-email" type="email" autocomplete="email" placeholder="you@studio.com" />
      </div>
      <div class="auth-field">
        <label for="auth-password">Password</label>
        <input id="auth-password" type="password" autocomplete="current-password" placeholder="Your password" />
      </div>
      <button class="auth-submit" type="button" data-action="submit-login">Sign in</button>
    </div>
    <p class="auth-foot"><button type="button" data-action="switch-login">Back to code sign-in</button></p>
  `
}

function renderRegister() {
  return `
    <div class="auth-tabs" role="tablist">
      <button class="auth-tab" role="tab" data-active="false" data-mode="login" type="button">Sign in</button>
      <button class="auth-tab" role="tab" data-active="true" data-mode="register" type="button">Sign up</button>
    </div>
    <div class="auth-head">
      <h2 class="auth-title">Create your account</h2>
      <p class="auth-sub">Start with 25 free credits. No credit card required.</p>
    </div>
    <div class="auth-body">
      <div class="auth-oauth-list" data-oauth-list>
        <button class="auth-oauth" type="button" data-action="google" data-provider="google">
          <span class="auth-g-mark" aria-hidden="true"></span>
          <span>Continue with Google</span>
        </button>
      </div>
      <div class="auth-divider">or</div>
      <div class="auth-field">
        <label for="auth-email">Email</label>
        <input id="auth-email" type="email" autocomplete="email" placeholder="you@studio.com" />
      </div>
      <div class="auth-field">
        <label for="auth-password">Password</label>
        <input id="auth-password" type="password" autocomplete="new-password" placeholder="At least 8 characters" />
      </div>
      <button class="auth-submit" type="button" data-action="submit-register">Create account</button>
    </div>
    <p class="auth-foot">Already have one? <button type="button" data-action="switch-login">Sign in</button></p>
  `
}

function renderEmailCode(email) {
  const safe = email ? escapeHtml(email) : 'your email'
  return `
    <div class="auth-head">
      <h2 class="auth-title">Enter your sign-in code</h2>
      <p class="auth-sub">We sent a 6-digit code to <strong>${safe}</strong>. It expires in 10 minutes.</p>
    </div>
    <div class="auth-body">
      <div class="auth-field">
        <label for="auth-code">Sign-in code</label>
        <input id="auth-code" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="6-digit code" maxlength="6" />
      </div>
      <button class="auth-submit" type="button" data-action="submit-email-code">Sign in</button>
      <p class="auth-foot"><button type="button" data-action="resend-code">Resend code</button></p>
    </div>
    <p class="auth-foot"><button type="button" data-action="switch-login">Back to sign in</button></p>
  `
}

function renderInner() {
  if (mode === 'register') return renderRegister()
  if (mode === 'password') return renderPasswordLogin()
  if (mode === 'email-code') return renderEmailCode(overlayEl?.dataset.email)
  return renderLogin()
}

function renderOverlay() {
  if (!overlayEl) {
    overlayEl = document.createElement('div')
    overlayEl.className = 'auth-overlay'
    overlayEl.dataset.open = 'false'
    overlayEl.setAttribute('role', 'dialog')
    overlayEl.setAttribute('aria-modal', 'true')
    overlayEl.setAttribute('aria-labelledby', 'authTitle')
    document.body.appendChild(overlayEl)
  }
  overlayEl.innerHTML = `
    <div class="auth-card" role="document">
      <button class="auth-close" type="button" aria-label="Close" data-action="close">
        <i class="ph ph-x"></i>
      </button>
      <a class="auth-brand" href="/" aria-label="mamamiya home">
        ${LOGO_SVG}
        <span class="auth-brand-wordmark">mamamiya</span>
      </a>
      ${renderInner()}
    </div>
  `
}

function bindHandlers() {
  if (overlayEl.dataset.handlersBound === 'true') return
  overlayEl.dataset.handlersBound = 'true'
  overlayEl.addEventListener('click', onClick)
  overlayEl.addEventListener('keydown', onKeydown)
}

function onClick(ev) {
  const action = ev.target.closest?.('[data-action]')?.dataset?.action
  const tabMode = ev.target.closest?.('.auth-tab')?.dataset?.mode
  if (tabMode && tabMode !== mode) {
    setMode(tabMode)
    return
  }
  // Backdrop click closes
  if (ev.target === overlayEl) {
    close()
    return
  }
  // H-186: provider-driven dispatch — every oauth button (static Google
  // included) carries data-provider; this branch runs BEFORE the action
  // switch. The legacy case 'google' below stays as belt-and-suspenders.
  const providerKey = ev.target.closest?.('[data-provider]')?.dataset?.provider
  if (providerKey) {
    handleOauth(providerKey)
    return
  }
  if (!action) return
  switch (action) {
    case 'close':
      close()
      break
    case 'switch-register':
      setMode('register')
      break
    case 'switch-login':
      setMode('login')
      break
    case 'google':
      handleGoogle()
      break
    case 'show-password':
      setMode('password')
      break
    case 'email-code':
      handleEmailCodeBegin()
      break
    case 'resend-code':
      handleEmailCodeBegin()
      break
    case 'submit-email-code':
      handleEmailCodeVerify()
      break
    case 'submit-login':
      handleSubmit('login')
      break
    case 'submit-register':
      handleSubmit('register')
      break
    default:
      break
  }
}

function toast(type, message) {
  eventBus.emit(EVENTS.TOAST_SHOW, { type, message })
}

function onAuthed(data) {
  saveAuth(data.user, data.token)
  const name = data.user?.name || data.user?.email || ''
  toast('success', `Welcome${name ? `, ${name}` : ''}!`)
  close()
}

function setSubmitting(on) {
  submitting = on
  overlayEl.querySelectorAll('.auth-submit').forEach((b) => {
    b.disabled = on
  })
}

/**
 * Persist the refresh half of a canonical token pair under the same keys
 * canonical-client.js uses, so the refresh grant works after ANY sign-in
 * path (email-code, password, signup, OAuth callback).
 */
function persistTokenPair(data) {
  if (data?.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token)
  if (data?.expires_at) localStorage.setItem(EXPIRES_KEY, String(data.expires_at))
}

async function handleSubmit(kind) {
  if (submitting) return
  const email = overlayEl.querySelector('#auth-email')?.value?.trim()
  const password = overlayEl.querySelector('#auth-password')?.value ?? ''
  if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    toast('warning', 'Enter a valid email address.')
    return
  }
  if (kind === 'register' && password.length < 8) {
    toast('warning', 'Password must be at least 8 characters.')
    return
  }
  if (kind === 'login' && !password) {
    toast('warning', 'Enter your password.')
    return
  }

  setSubmitting(true)
  try {
    const data = kind === 'login' ? await loginPassword(email, password) : await signupPassword(email, password)
    recordCreatorAuthMetric(kind === 'login' ? 'password_login' : 'password_signup', 'used')
    persistTokenPair(data)
    onAuthed({ user: data.customer, token: data.token })
  } catch (err) {
    toast('error', err?.message || `${kind === 'login' ? 'Sign in' : 'Sign up'} failed`)
    log.warn('Auth submit failed:', kind, err?.message)
  } finally {
    setSubmitting(false)
  }
}

/**
 * H-147 email-code begin: validate the email on the login form, ask the
 * canonical customer realm to send a 6-digit code, then switch to the code
 * entry step. Server errors are surfaced verbatim — no fake "sent" state.
 * Also handles "Resend code" from the code-entry step (uses the stored email).
 */
async function handleEmailCodeBegin() {
  if (submitting) return
  const email =
    overlayEl.querySelector('#auth-email')?.value?.trim() || overlayEl.dataset.email || ''
  if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    toast('warning', 'Enter a valid email first, then tap "Email me a sign-in code".')
    return
  }
  setSubmitting(true)
  try {
    await beginEmailCode(email)
    recordCreatorAuthMetric('email_code_begin', 'used')
    overlayEl.dataset.email = email
    setMode('email-code')
  } catch (err) {
    toast('error', err?.message || 'Could not send the sign-in code')
    log.warn('Email-code begin failed:', err?.message)
  } finally {
    setSubmitting(false)
  }
}

/**
 * H-147 email-code verify: exchange email + 6-digit code for the canonical
 * token pair, persist the refresh token (same keys as the canonical OAuth
 * client), then take the shared saveAuth success path.
 */
async function handleEmailCodeVerify() {
  if (submitting) return
  const email = overlayEl.dataset.email || ''
  const code = overlayEl.querySelector('#auth-code')?.value?.trim() ?? ''
  if (!/^\d{6}$/.test(code)) {
    toast('warning', 'Enter the 6-digit code from your email.')
    return
  }
  setSubmitting(true)
  try {
    const data = await verifyEmailCode(email, code)
    recordCreatorAuthMetric('email_code_verify', 'used')
    persistTokenPair(data)
    onAuthed({ user: data.customer, token: data.token })
  } catch (err) {
    toast('error', err?.message || 'Sign-in failed')
    log.warn('Email-code verify failed:', err?.message)
  } finally {
    setSubmitting(false)
  }
}

/**
 * OAuth sign-in — always the canonical /authorize authorization-code
 * redirect (PKCE S256). H-186 generalized the Google-only handler: the
 * provider key rides the additive `provider` param (omitted for google, so
 * pre-H-185 backends behave identically). The GIS one-tap + legacy
 * /api/auth/google chain is retired; /login/google is 501 on the canonical
 * realm by design.
 */
async function handleOauth(providerKey) {
  if (submitting) return
  setSubmitting(true)
  try {
    recordCreatorAuthMetric('canonical_authorize', 'used', undefined, { provider: providerKey })
    window.location.assign(await createCreatorHubCanonicalAuthorizeUrl({ provider: providerKey }))
  } catch (err) {
    recordCreatorAuthMetric('canonical_authorize', 'rejected', 'provider_unavailable', { provider: providerKey })
    toast('error', err?.message || 'Sign-in failed')
    setSubmitting(false)
  }
}

// DOM-contract red line: case 'google' keeps routing here.
async function handleGoogle() {
  return handleOauth('google')
}

/**
 * H-186 provider-driven hydration: on open / mode-switch, fetch the
 * /providers descriptor list and swap ONLY the oauth-list children (never a
 * full re-render — that would wipe #auth-email's value and focus). Any
 * failure leaves the static Google button byte-identical (grayscale-safe
 * against pre-H-185 backends, which return no descriptor array).
 */
let oauthHydrationGen = 0

function oauthButtonHtml(descriptor) {
  const key = descriptor.key
  const isGoogle = key === 'google'
  // Google's mark keeps its shipped class (red line); other providers use the
  // auth-mark-<key> convention, or an <img> when the descriptor ships an icon.
  const mark = descriptor.icon
    ? `<img class="auth-mark-img" src="${escapeHtml(descriptor.icon)}" alt="" aria-hidden="true" />`
    : `<span class="${isGoogle ? 'auth-g-mark' : `auth-mark-${escapeHtml(key)}`}" aria-hidden="true"></span>`
  return `<button class="auth-oauth" type="button" data-action="${isGoogle ? 'google' : 'oauth'}" data-provider="${escapeHtml(key)}">${mark}<span>Continue with ${escapeHtml(descriptor.display_name)}</span></button>`
}

async function hydrateOauthButtons() {
  const gen = ++oauthHydrationGen
  const providers = await fetchCreatorHubOauthProviders()
  // Generation guard: a late response after a mode switch (password /
  // email-code steps have no oauth block) must not write.
  if (gen !== oauthHydrationGen || !providers || !overlayEl) return
  if (overlayEl.dataset.open !== 'true') return
  if (mode !== 'login' && mode !== 'register') return
  const list = overlayEl.querySelector('[data-oauth-list]')
  if (!list) return
  const tpl = document.createElement('template')
  tpl.innerHTML = providers.map(oauthButtonHtml).join('')
  list.replaceChildren(...tpl.content.childNodes)
}

function onKeydown(ev) {
  if (ev.key === 'Escape') {
    close()
  }
}

function setMode(next) {
  mode = next
  renderOverlay()
  // Re-focus first focusable for a11y
  const firstInput = overlayEl.querySelector('input, button[data-action="google"]')
  firstInput?.focus?.()
  // H-186: refresh provider buttons after every mode render (non-blocking;
  // the generation guard discards the write for oauth-less steps).
  hydrateOauthButtons()
}

export function open() {
  if (!overlayEl) renderOverlay()
  overlayEl.dataset.open = 'true'
  document.documentElement.style.overflow = 'hidden'
  // H-147: pre-fill the Shopify email hint if the storefront provided one
  // (UX convenience only — never a credential).
  const hint = emailHint()
  const emailInput = overlayEl.querySelector('#auth-email')
  if (hint && emailInput && !emailInput.value) emailInput.value = hint
  // Focus first input on open
  setTimeout(() => {
    const focusEl = overlayEl.querySelector('input, button[data-action="google"]')
    focusEl?.focus?.()
  }, 50)
  // H-186: provider-driven buttons hydrate after first paint — the static
  // Google button is already visible, this only swaps in the descriptor list.
  hydrateOauthButtons()
}

export function close() {
  if (!overlayEl) return
  overlayEl.dataset.open = 'false'
  document.documentElement.style.overflow = ''
  // Reset to login on next open
  mode = 'login'
  store.setState('auth.showLoginModal', false)
}

/**
 * Initialize: subscribe to store state and toggle overlay.
 * Idempotent — safe to call once on app boot.
 */
export function initLoginModal() {
  renderOverlay()
  bindHandlers()

  // Subscribe to store changes — show modal when flag flips true
  store.subscribe?.('auth.showLoginModal', (val) => {
    if (val) {
      open()
    } else {
      // Hide if flag set to false externally
      if (overlayEl?.dataset.open === 'true') {
        overlayEl.dataset.open = 'false'
        document.documentElement.style.overflow = ''
      }
    }
  })

  // Apply current state on init (in case flag was already true)
  if (store.getState('auth.showLoginModal')) {
    open()
  }

  log.info('Login modal initialized')
}

export default { initLoginModal, open, close }
